import { useState } from 'react';
import { Plus, Trash2, Info } from 'lucide-react';
import type { TrabajosConfig } from '@/api/installation.api';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { Modal } from '@/components/ui/Modal';
import { etiquetaCobertura } from '@/lib/cierre-validacion';

/**
 * Pestaña TRABAJOS de la configuración — portada de silvai-chat.
 *
 * Diferencias con silvai-chat:
 *  - Arriba va el interruptor de la validación del cierre, APAGADO por defecto.
 *    En ELECTRO MEDIA esa puerta es una decisión del cliente; acá cada negocio
 *    decide si sus trabajos llevan venta que validar.
 *  - Los catálogos nacen vacíos y los ejemplos son neutros.
 */

/** Id local para filas nuevas (el server solo exige que sea único y estable). */
const nuevoId = (prefijo: string) => `${prefijo}-${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;

export function TrabajosTab({
  value,
  onChange,
}: {
  value: TrabajosConfig;
  onChange: (patch: Partial<TrabajosConfig>) => void;
}) {
  // Confirmación antes de borrar: un catálogo borrado de un clic deja trabajos
  // viejos apuntando a algo que ya no existe.
  const [porBorrar, setPorBorrar] = useState<{
    tipo: 'modelo' | 'formaPago' | 'nivel' | 'causa';
    id: string;
    nombre: string;
  } | null>(null);

  const modelos = value.modelos ?? [];
  const formasPago = value.formasPago ?? [];
  const niveles = value.nivelesResolucion ?? [];
  const causas = value.causasRaiz ?? [];
  const pagosActivos = formasPago.filter((f) => f.activa).length;
  const conValidacion = value.cierreConValidacion === true;

  function confirmarBorrado() {
    if (!porBorrar) return;
    const { tipo, id } = porBorrar;
    if (tipo === 'modelo') onChange({ modelos: modelos.filter((m) => m.id !== id) });
    if (tipo === 'formaPago') onChange({ formasPago: formasPago.filter((f) => f.id !== id) });
    if (tipo === 'nivel') onChange({ nivelesResolucion: niveles.filter((n) => n.id !== id) });
    if (tipo === 'causa') onChange({ causasRaiz: causas.filter((c) => c.id !== id) });
    setPorBorrar(null);
  }

  return (
    <div className="space-y-4">
      {/* ----------------------- VALIDACIÓN DEL CIERRE ----------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Cierre con cobro</CardTitle>
          <CardDescription>
            Encendido, el técnico no cierra el trabajo: al tocar «Terminé» carga el modelo, el número de serie y
            el resultado, y el trabajo queda «Por validar» hasta que alguien de la oficina cargue el monto, la
            forma de pago y el vendedor. Apagado, «Terminé» cierra el trabajo como siempre.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex items-center gap-2">
            <Toggle
              checked={conValidacion}
              label="Pedir el cobro para cerrar los trabajos"
              onChange={(cierreConValidacion) => onChange({ cierreConValidacion })}
            />
            <span className="text-sm text-slate-700 dark:text-slate-200">
              {conValidacion ? 'Los trabajos se cierran desde la oficina' : 'El técnico cierra el trabajo'}
            </span>
          </div>
          {conValidacion && (modelos.length === 0 || pagosActivos === 0) && (
            <p className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              Con esto encendido hacen falta al menos un modelo y una forma de pago encendida. Sin modelos el
              técnico no puede terminar; sin formas de pago la oficina no puede cerrar.
            </p>
          )}
        </CardBody>
      </Card>

      {/* ------------------------------ MODELOS ------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>Modelos y su garantía</CardTitle>
          <CardDescription>
            Los modelos que el técnico puede elegir al cerrar un trabajo, y cuánto tiempo quedan cubiertos por
            garantía.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {modelos.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay ningún modelo cargado.
            </p>
          )}

          {modelos.map((m, i) => (
            <div
              key={m.id}
              className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-[1fr_auto_auto] sm:items-end dark:border-slate-700"
            >
              <Input
                label="Nombre del modelo"
                value={m.nombre}
                placeholder="Nombre del modelo"
                onChange={(e) => {
                  const next = [...modelos];
                  next[i] = { ...m, nombre: e.target.value };
                  onChange({ modelos: next });
                }}
              />
              <label className="block">
                <Input
                  label="Garantía (meses)"
                  type="number"
                  min={1}
                  max={600}
                  className="sm:w-32"
                  value={String(m.mesesCobertura)}
                  onChange={(e) => {
                    const next = [...modelos];
                    // Se guarda tal cual se escribe; el server lo acota al guardar.
                    next[i] = { ...m, mesesCobertura: Number(e.target.value) };
                    onChange({ modelos: next });
                  }}
                />
                <span className="mt-1 block text-[11px] text-slate-500">
                  {Number.isFinite(m.mesesCobertura) && m.mesesCobertura > 0
                    ? etiquetaCobertura(Math.floor(m.mesesCobertura))
                    : 'Escriba cuántos meses dura la garantía'}
                </span>
              </label>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar el modelo ${m.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'modelo', id: m.id, nombre: m.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            onClick={() => onChange({ modelos: [...modelos, { id: nuevoId('mod'), nombre: '', mesesCobertura: 12 }] })}
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar modelo
          </Button>
        </CardBody>
      </Card>

      {/* --------------------------- FORMAS DE PAGO --------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Formas de pago</CardTitle>
          <CardDescription>
            Las opciones que ve la oficina al cerrar un trabajo con cobro. Apagar una la saca de la lista sin
            tocar los trabajos que ya la usaron.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {formasPago.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay formas de pago cargadas.
            </p>
          )}

          {formasPago.map((f, i) => (
            <div
              key={f.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-700"
            >
              <Input
                className="min-w-[10rem] flex-1"
                aria-label="Nombre de la forma de pago"
                value={f.nombre}
                placeholder="Nombre de la forma de pago"
                onChange={(e) => {
                  const next = [...formasPago];
                  next[i] = { ...f, nombre: e.target.value };
                  onChange({ formasPago: next });
                }}
              />
              <div className="flex items-center gap-2">
                <Toggle
                  checked={f.activa}
                  label={`Usar la forma de pago ${f.nombre || 'sin nombre'}`}
                  onChange={(activa) => {
                    const next = [...formasPago];
                    next[i] = { ...f, activa };
                    onChange({ formasPago: next });
                  }}
                />
                <span className="text-xs text-slate-600 dark:text-slate-300">
                  {f.activa ? 'Se puede usar' : 'Apagada'}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar la forma de pago ${f.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'formaPago', id: f.id, nombre: f.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            onClick={() => onChange({ formasPago: [...formasPago, { id: nuevoId('pago'), nombre: '', activa: true }] })}
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar forma de pago
          </Button>
        </CardBody>
      </Card>

      {/* ----------------------- CÓMO SE RESOLVIÓ ----------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Cómo se resolvió la garantía</CardTitle>
          <CardDescription>
            Con el cierre con cobro encendido, el técnico elige una de estas opciones al cerrar una garantía y la
            oficina la confirma o la corrige. La clave corta es lo que queda guardado en el trabajo.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {niveles.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay opciones cargadas.
            </p>
          )}
          {niveles.map((n, i) => (
            <div
              key={n.id}
              className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-[7rem_1fr_auto_auto] sm:items-end dark:border-slate-700"
            >
              <Input
                label="Clave corta"
                value={n.codigo}
                placeholder="Clave"
                onChange={(e) => {
                  const next = [...niveles];
                  next[i] = { ...n, codigo: e.target.value };
                  onChange({ nivelesResolucion: next });
                }}
              />
              <Input
                label="Cómo se ve en pantalla"
                value={n.nombre}
                placeholder="Cómo se resolvió"
                onChange={(e) => {
                  const next = [...niveles];
                  next[i] = { ...n, nombre: e.target.value };
                  onChange({ nivelesResolucion: next });
                }}
              />
              <div className="flex items-center gap-2">
                <Toggle
                  checked={n.activo}
                  label={`Usar la opción ${n.nombre || 'sin nombre'}`}
                  onChange={(activo) => {
                    const next = [...niveles];
                    next[i] = { ...n, activo };
                    onChange({ nivelesResolucion: next });
                  }}
                />
                <span className="text-xs text-slate-600 dark:text-slate-300">{n.activo ? 'En uso' : 'Apagada'}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar la opción ${n.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'nivel', id: n.id, nombre: n.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              onChange({ nivelesResolucion: [...niveles, { id: nuevoId('niv'), codigo: '', nombre: '', activo: true }] })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar opción
          </Button>
        </CardBody>
      </Card>

      {/* -------------------------- POR QUÉ FALLÓ -------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Por qué falló</CardTitle>
          <CardDescription>
            La causa de la garantía. El técnico propone una y la oficina la confirma o la corrige; el reporte usa la
            de la oficina.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {causas.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay causas cargadas.
            </p>
          )}
          {causas.map((c, i) => (
            <div
              key={c.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-700"
            >
              <Input
                className="min-w-[12rem] flex-1"
                aria-label="Nombre de la causa"
                value={c.nombre}
                placeholder="Nombre de la causa"
                onChange={(e) => {
                  const next = [...causas];
                  next[i] = { ...c, nombre: e.target.value };
                  onChange({ causasRaiz: next });
                }}
              />
              <div className="flex items-center gap-2">
                <Toggle
                  checked={c.activo}
                  label={`Usar la causa ${c.nombre || 'sin nombre'}`}
                  onChange={(activo) => {
                    const next = [...causas];
                    next[i] = { ...c, activo };
                    onChange({ causasRaiz: next });
                  }}
                />
                <span className="text-xs text-slate-600 dark:text-slate-300">{c.activo ? 'En uso' : 'Apagada'}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar la causa ${c.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'causa', id: c.id, nombre: c.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() => onChange({ causasRaiz: [...causas, { id: nuevoId('causa'), nombre: '', activo: true }] })}
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar causa
          </Button>
        </CardBody>
      </Card>

      <Modal open={porBorrar !== null} onClose={() => setPorBorrar(null)} title="¿Quitar de la lista?">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Se va a quitar <strong>{porBorrar?.nombre || 'este elemento'}</strong> de la lista. Los trabajos que ya
          lo usaron conservan lo suyo; solo deja de aparecer para los nuevos.
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          El cambio se aplica cuando apriete <strong>Guardar cambios</strong>.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setPorBorrar(null)}>
            Cancelar
          </Button>
          <Button type="button" variant="danger" onClick={confirmarBorrado}>
            Quitar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
